import type { Client } from '@urql/core'
import { chunk } from 'lodash'
import type { WorkflowTriggerInput } from 'app/generated/graphql'
import {
  LIST_WORKFLOW_RUNS_QUERY,
  LOOKBACK_APPOINTMENTS_QUERY,
  LOOKBACK_CONTACTS_QUERY,
  PREVIEW_WORKFLOW_TRIGGER_QUERY,
} from 'app/lib/workflows/graphql'
import { buildRows, type ContactState, type LookbackRow, type SourceState } from './lookbackRows'
import type { LookbackMatch, LookbackRejection, PreviewWindow } from './types'

export type LookbackLoad = {
  rows: LookbackRow[]
  /** How many source events the engine examined in the window. */
  considered: number
  rejections: LookbackRejection[]
  /** Every match in the window, loaded or not. */
  total: number
  /** The day the engine's event history begins. Matches before it are rebuilt from records. */
  eventHistoryStart: string | null
}

export const DAY_MS = 24 * 60 * 60 * 1000
export const PAGE_SIZE = 200
// The search DSL takes OR lists; keep each short enough to stay a sane URL (urql sends GET).
const ID_CHUNK = 40

const NETWORK = { requestPolicy: `network-only` } as const

/** The engine's own answer to "who would this trigger have fired for" (TRK-5826). */
export async function fetchPreview(
  client: Client,
  trigger: WorkflowTriggerInput,
  locationId: string,
  window: PreviewWindow,
  page = 0,
): Promise<{
  matches: LookbackMatch[]
  total: number
  examined: number
  rejections: LookbackRejection[]
  eventHistoryStart: string
}> {
  const result = await client
    .query(
      PREVIEW_WORKFLOW_TRIGGER_QUERY,
      {
        trigger,
        locationId,
        windowStart: window.start.toISOString(),
        windowEnd: window.end.toISOString(),
        pagination: { limit: PAGE_SIZE, page },
      },
      NETWORK,
    )
    .toPromise()
  if (result.error) throw result.error
  const preview = result.data?.previewWorkflowTrigger
  if (!preview) throw new Error(`The trigger preview returned nothing`)
  return {
    matches: preview.matches.map((match) => ({
      id: match.eventId ?? `${match.sourceType}:${match.sourceId}:${match.date}`,
      sourceType: match.sourceType,
      sourceId: match.sourceId,
      occurredAt: match.date,
      basis: match.reconstructedFromRecord ? `record` : `event`,
      matchedValues: (match.conditionValues ?? {}) as Record<string, unknown>,
      contactId: match.contactId ?? null,
    })),
    total: preview.total,
    examined: preview.examinedCount,
    rejections: preview.conditionRejections.map((rejection) => ({
      filter: {
        attribute: rejection.attribute,
        operator: rejection.operator,
        value: rejection.value == null ? undefined : String(rejection.value),
      },
      count: rejection.rejectedCount,
    })),
    eventHistoryStart: preview.eventHistoryStart,
  }
}

export async function fetchContacts(client: Client, contactIds: string[]): Promise<ContactState[]> {
  const contacts: ContactState[] = []
  for (const ids of chunk(contactIds, ID_CHUNK)) {
    const result = await client
      .query(
        LOOKBACK_CONTACTS_QUERY,
        { search: ids.map((id) => `id:"${id}"`).join(` OR `), pagination: { limit: PAGE_SIZE, page: 0 } },
        NETWORK,
      )
      .toPromise()
    if (result.error) throw result.error
    for (const hit of result.data?.contacts?.hits ?? [])
      contacts.push({
        id: hit.id,
        name: hit.name || `Unnamed contact`,
        archived: !!hit.archived,
        pipelineIds: (hit.pipelines ?? []).map((pipeline) => pipeline.id),
      })
  }
  return contacts
}

/** Today's state of the matched appointments, keyed by appointment id. */
export async function fetchSources(client: Client, appointmentIds: string[]): Promise<Record<string, SourceState>> {
  const sources: Record<string, SourceState> = {}
  for (const ids of chunk(appointmentIds, ID_CHUNK)) {
    const result = await client
      .query(
        LOOKBACK_APPOINTMENTS_QUERY,
        { search: ids.map((id) => `id:"${id}"`).join(` OR `), pagination: { limit: PAGE_SIZE, page: 0 } },
        NETWORK,
      )
      .toPromise()
    if (result.error) throw result.error
    for (const hit of result.data?.appointments?.hits ?? [])
      sources[hit.id] = { type: hit.type, status: hit.status, date: hit.date, time: hit.time }
  }
  return sources
}

export async function fetchOpenRunContactIds(client: Client, workflowId: string | undefined): Promise<Set<string>> {
  const contactIds = new Set<string>()
  if (!workflowId) return contactIds
  const result = await client
    .query(
      LIST_WORKFLOW_RUNS_QUERY,
      {
        search: `workflowId:${workflowId} AND (status:running OR status:waiting)`,
        pagination: { limit: PAGE_SIZE, page: 0 },
        sort: { field: `startedAt`, direction: `DESC` },
      },
      NETWORK,
    )
    .toPromise()
  if (result.error) throw result.error
  for (const run of result.data?.workflowRuns?.hits ?? []) if (run.contactId) contactIds.add(run.contactId)
  return contactIds
}

export const contactIdsOf = (matches: LookbackMatch[]): string[] => [
  ...new Set(matches.flatMap((match) => (match.contactId ? [match.contactId] : []))),
]

export const appointmentIdsOf = (matches: LookbackMatch[]): string[] =>
  matches.filter((match) => match.sourceType === `Appointment`).map((match) => match.sourceId)

/** Loads pages of matches; `soonest` reads the engine's newest-first list from its far end. */
export async function loadLookback(
  client: Client,
  trigger: WorkflowTriggerInput,
  locationId: string,
  window: PreviewWindow,
  workflowId: string | undefined,
  targetPipelineId: string | undefined,
  pages = { from: 0, count: 1 },
  order: `newest` | `soonest` = `newest`,
): Promise<LookbackLoad> {
  // The first answer carries the total, which says where the far end is.
  const probePage = order === `soonest` ? 0 : pages.from
  const probe = await fetchPreview(client, trigger, locationId, window, probePage)
  const lastPage = Math.max(0, Math.ceil(probe.total / PAGE_SIZE) - 1)
  const matches: LookbackMatch[] = []
  for (let logical = pages.from; logical < pages.from + pages.count && logical <= lastPage; logical++) {
    const page = order === `soonest` ? lastPage - logical : logical
    const preview = page === probePage ? probe : await fetchPreview(client, trigger, locationId, window, page)
    matches.push(...(order === `soonest` ? [...preview.matches].reverse() : preview.matches))
  }
  const [contacts, sources, openRunContactIds] = await Promise.all([
    fetchContacts(client, contactIdsOf(matches)),
    fetchSources(client, appointmentIdsOf(matches)),
    fetchOpenRunContactIds(client, workflowId),
  ])
  return {
    rows: buildRows(matches, { contacts, sources, openRunContactIds, targetPipelineId }),
    considered: probe.examined,
    rejections: probe.rejections,
    total: probe.total,
    eventHistoryStart: probe.eventHistoryStart,
  }
}
